import { useState } from "react";
import { Nav } from "../components/Nav";
import { AltHero } from "../components/AltHero";
import { Footer } from "../components/Footer";
import { PostDownloadOverlay } from "../components/PostDownloadOverlay";
import { ContactModal } from "../components/ContactModal";
import { WhatYouCanBuild } from "../components/sections/WhatYouCanBuild";
import { HowItWorks } from "../components/sections/HowItWorks";
import { VsJython } from "../components/sections/VsJython";
import { RequirementsInstall } from "../components/sections/RequirementsInstall";
import { Features } from "../components/sections/Features";
import { Pricing } from "../components/sections/Pricing";
import { WhoWeAre } from "../components/sections/WhoWeAre";
import { FinalCTA } from "../components/sections/FinalCTA";
import { useRelease } from "../hooks/use-release";
import { useDetectedOS } from "../hooks/use-detected-os";
import type { Platform } from "../lib/release";
import { artifactFilename, artifactUrl } from "../lib/release";

const STRIPE_CHECKOUT_URL = "https://buy.stripe.com/5kQfZi1w95EzeRH4lG5Ne02";

export function ProductPage() {
  const [chooserOpen, setChooserOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [downloadedPlatform, setDownloadedPlatform] = useState<Platform | null>(
    null,
  );
  const release = useRelease();
  const detected = useDetectedOS();

  const handleBuy = () => {
    window.open(STRIPE_CHECKOUT_URL, "_blank", "noopener,noreferrer");
  };

  // Triggered after a download click. The browser handles the actual
  // transfer via <a href download> on the source link; this only records
  // which platform was clicked and shows the post-download overlay.
  // Always returns true when the manifest is loaded so platforms with
  // synthesized (not-yet-built) URLs still proceed — the browser will
  // either start the download or surface the bucket's 404 honestly.
  const handleDownload = (platform: Platform): boolean => {
    if (release.status !== "ready" || !release.manifest) return false;
    setDownloadedPlatform(platform);
    setPostOpen(true);
    setChooserOpen(false);
    return true;
  };

  const downloadedFilename =
    release.manifest && downloadedPlatform
      ? artifactFilename(release.manifest, downloadedPlatform)
      : null;
  const downloadedUrl =
    release.manifest && downloadedPlatform
      ? artifactUrl(release.manifest, downloadedPlatform)
      : null;

  return (
    <>
      <Nav onContactClick={() => setContactOpen(true)} />
      <AltHero
        chooserOpen={chooserOpen}
        setChooserOpen={setChooserOpen}
        onBuyClick={handleBuy}
        release={release}
        detected={detected}
        onDownload={handleDownload}
      />
      <WhatYouCanBuild />
      <HowItWorks />
      <VsJython showRow2={true} onContactClick={() => setContactOpen(true)} />
      <RequirementsInstall />
      <Features />
      <Pricing
        onBuyClick={handleBuy}
        onDownloadClick={() => {
          document.getElementById("final-cta")?.scrollIntoView({ behavior: "smooth" });
          setChooserOpen(true);
        }}
        onContactClick={() => setContactOpen(true)}
      />
      <WhoWeAre onContactClick={() => setContactOpen(true)} />
      <FinalCTA
        chooserOpen={chooserOpen}
        setChooserOpen={setChooserOpen}
        onBuyClick={handleBuy}
        release={release}
        detected={detected}
        onDownload={handleDownload}
      />
      <Footer release={release} onContactClick={() => setContactOpen(true)} />
      <PostDownloadOverlay
        open={postOpen}
        onClose={() => setPostOpen(false)}
        filename={downloadedFilename}
        url={downloadedUrl}
      />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
}
